import * as PIXI from 'pixi.js-legacy';

interface IConfig {
    container?: HTMLDivElement | null;
    width?: number | null;
    height?: number | null;
    expValue: number;
    padding: number;
    backgroundColor: number;
    axisColor: number;
    curveColor: number;
    diagonalColor: number;
    arrowColor: number;
    labelColor: number;
}

/**
 * 指数曲线图
 * */
export default class ExpCurveGraph {
    protected container: HTMLDivElement | any = null;
    protected curveGraphics: PIXI.Graphics | any = null;
    protected diagonalGraphics: PIXI.Graphics | any = null;
    protected axisGraphics: PIXI.Graphics | any = null;
    protected arrowGraphics: PIXI.Graphics | any = null;
    protected labelContainer: PIXI.Container | any = null;
    protected centerCircleGraphics: PIXI.Graphics | any = null;
    protected initialized = false;
    protected graphWidth = 0;
    protected graphHeight = 0;
    protected app: PIXI.Application | any = null;
    protected config:IConfig | any = {};

    /**
    * 构造函数
    * @param {Object} options - 配置选项
    * @param {HTMLDivElement} options.container - 容器DOM元素
    * @param {number} options.width - 画布宽度
    * @param {number} options.height - 画布高度
    * @param {number} options.expValue - 初始EXP值 (0.1-0.9)
    */

    constructor(options: { container: HTMLDivElement | any;expValue: number;curveColor: number;}) {
       if(options.container){
            this.config = {
                container: options.container,
                expValue: Math.min(Math.max(options.expValue || 0.5, 0.1), 0.9),
                padding: 0,
                backgroundColor: 0x0f1428,
                axisColor: 0xA2A6B1,
                curveColor: options.curveColor || 0x6ab0ff,
                diagonalColor:0xA2A6B1,
                arrowColor: 0xA2A6B1,
                labelColor: 0xFFFFFFE5
            };

            // Pixi.js相关对象
            this.app = null;
            this.container = null;
            this.curveGraphics = null;
            this.diagonalGraphics = null;
            this.axisGraphics = null;
            this.arrowGraphics = null;
            this.labelContainer = null;
            this.centerCircleGraphics = null;
            // 状态标志
            this.initialized = false;
            this.initContainer();
            this.initPixiApp();
       }
    }

    /**
    * 初始化画布容器
    */
    protected initContainer() {
        if (!this.config.container) return;
        const rect = this.config.container.getBoundingClientRect();
        this.config.width = rect.width || 0; // 画布宽度
        this.config.height = rect.height || 0; // 画布高度
         // 计算后的图形尺寸
        this.graphWidth = this.config.width - 2 * this.config.padding;
        this.graphHeight = this.config.height - 2 * this.config.padding;
    }

    protected initPixiApp() {
        if (!this.config.container) return;
        this.app = new PIXI.Application({ 
            width: this.config.width, 
            height: this.config.height, 
            transparent: true,
            resolution: window.devicePixelRatio || 2,
            autoDensity: true,
            forceCanvas:true,
            antialias: true,
        })
        this.config.container.appendChild(this.app.view);

        // 设置视图样式
        this.app.view.style.width = '100%';
        this.app.view.style.height = '100%';
        this.app.view.style.display = 'block';
        
        // 创建容器
        this.container = new PIXI.Container();
        this.app.stage.addChild(this.container);

        this.axisGraphics = new PIXI.Graphics();
        this.diagonalGraphics = new PIXI.Graphics();
        this.curveGraphics = new PIXI.Graphics();
        this.arrowGraphics = new PIXI.Graphics();
        this.labelContainer = new PIXI.Container();
        this.centerCircleGraphics = new PIXI.Graphics();
        
        // 添加到容器
        this.container.addChild(this.axisGraphics);
        this.container.addChild(this.diagonalGraphics);
        this.container.addChild(this.arrowGraphics);
        this.container.addChild(this.curveGraphics);
        this.container.addChild(this.labelContainer);
        this.container.addChild(this.centerCircleGraphics);

        this.initialized = true;
        // 绘制静态元素
        this.drawAxes();
        this.drawArrowsAndLabels();
        this.drawDiagonal();
        this.drawCenterCircle();
        this.updateCurve(this.config.expValue);
        
    }


    /**
    * 绘制坐标轴
    */
    drawAxes() {
        const g = this.axisGraphics;
        const padding = this.config.padding;
        const width = this.config.width;
        const height = this.config.height;
        
        g.clear();
        g.lineStyle(2, this.config.axisColor, 0.6);
        
        // X轴（水平线）
        g.moveTo(padding, height / 2);
        g.lineTo(width - padding, height / 2);
        
        // Y轴（垂直线）
        g.moveTo(width / 2, padding);
        g.lineTo(width / 2, height - padding);
    }

    drawCenterCircle() { 
        const centerPoint = this.centerCircleGraphics;
        const centerX = this.config.width / 2;
        const centerY = this.config.height / 2;
        centerPoint.clear();
        centerPoint.beginFill(0xffffff);
        centerPoint.drawCircle(centerX, centerY, 8);
    }

    /**
    * 绘制箭头和字母标签
    */
    drawArrowsAndLabels() {
        const g = this.arrowGraphics;
        const padding = this.config.padding;
        const width = this.config.width;
        const height = this.config.height;
        
        g.clear();
        
        // 绘制X轴箭头（右侧）
        g.lineStyle(0);
        g.beginFill(this.config.arrowColor, 0.8);
        g.drawPolygon([
            width - padding, height / 2,           // 箭头尖端
            width - padding - 10, height / 2 - 5,  // 箭头左上方
            width - padding - 10, height / 2 + 5   // 箭头左下方
        ]);
        g.endFill();
        
        // 绘制Y轴箭头（上方）
        g.beginFill(this.config.arrowColor, 0.8);
        g.drawPolygon([
            width / 2, padding,           // 箭头尖端
            width / 2 - 5, padding + 10,  // 箭头左下方
            width / 2 + 5, padding + 10   // 箭头右下方
        ]);
        g.endFill();
        
        // 清除之前的标签
        this.labelContainer.removeChildren();
        
        // 创建X字母标签（在X轴箭头右侧）
        const xLabelStyle = new PIXI.TextStyle({
            fontFamily: 'Arial',
            fontSize: 16,
            fill: this.config.labelColor,
            fontWeight: 'normal',
            align: 'center'
        });
        
        const xLabel = new PIXI.Text('X', xLabelStyle);
        xLabel.x = width - padding - 12;
        xLabel.y = height / 2 + 4 ;
        this.labelContainer.addChild(xLabel);
        
        // 创建Y字母标签（在Y轴箭头上方）
        const yLabelStyle = new PIXI.TextStyle({
            fontFamily: 'Arial',
            fontSize: 16,
            fill: this.config.labelColor,
            fontWeight: 'normal',
            align: 'center'
        });
        
        const yLabel = new PIXI.Text('Y', yLabelStyle);
        yLabel.x = width / 2 + 6;
        yLabel.y = padding ;
        this.labelContainer.addChild(yLabel);
    }

    /**
    * 绘制对角线（从左下到右上）
    */
    drawDiagonal() {
        const g = this.diagonalGraphics;
        const padding = this.config.padding;
        const width = this.config.width;
        const height = this.config.height;
        
        g.clear();
        
        // 计算起点和终点坐标
        const startX = padding;
        const startY = height - padding;
        const endX = width - padding;
        const endY = padding;
        
        // 计算对角线长度
        const dx = endX - startX;
        const dy = endY - startY;
        const length = Math.sqrt(dx * dx + dy * dy);
        
        // 虚线参数
        const dashLength = 8; // 虚线段的长度
        const gapLength = 4;  // 间隔的长度
        
        // 计算单位向量
        const unitX = dx / length;
        const unitY = dy / length;
        
        // 计算需要绘制的虚线数量
        const dashCount = Math.floor(length / (dashLength + gapLength));
        
        // 设置线条样式
        g.lineStyle(1.5, this.config.diagonalColor, 0.4);
        
        // 绘制虚线
        for (let i = 0; i < dashCount; i++) {
            // 计算当前虚线的起点
            const dashStartX = startX + (dashLength + gapLength) * i * unitX;
            const dashStartY = startY + (dashLength + gapLength) * i * unitY;
            
            // 计算当前虚线的终点
            const dashEndX = dashStartX + dashLength * unitX;
            const dashEndY = dashStartY + dashLength * unitY;
            
            // 绘制虚线
            g.moveTo(dashStartX, dashStartY);
            g.lineTo(dashEndX, dashEndY);
        }
        
        // 绘制最后一段（可能不完整）
        const lastDashStartX = startX + (dashLength + gapLength) * dashCount * unitX;
        const lastDashStartY = startY + (dashLength + gapLength) * dashCount * unitY;
        
        // 如果最后一段有内容，则绘制
        if (lastDashStartX < endX || lastDashStartY < endY) {
            g.moveTo(lastDashStartX, lastDashStartY);
            g.lineTo(endX, endY);
        }
    }

    /**
    * 根据新公式计算EXP曲线上的点
    * @param {number} x - 输入值 (-1 到 1)
    * @param {number} exp - EXP值 (0.1 到 0.9)
    * @returns {number} 输出值 (-1 到 1)
    */
    calculateExpValue(x:number, exp:number) {
        let y ;
        let absResult = Math.abs(x);
        if(x == 0){
          y = 0
        }else {
          if (exp <= 0.5) {
            y = Math.pow(absResult, (1 + (0.5 - exp) * 0.75 / exp)) * x / absResult;
          } else {
            y = Math.pow(absResult, 1 / (1 + (exp - 0.5) * 8 / exp)) * x / absResult;
          }
        }
        return y;
    }
    
    /**
     * 更新EXP曲线
     * @param {number} expValue - EXP值 (0.1-0.9)
     */
    updateCurve(expValue:number) {
        if (!this.initialized) {
            console.error('ExpCurveGraphNewFormula尚未初始化');
            return;
        }
        
        // 确保EXP值在有效范围内
        const exp = Math.min(Math.max(expValue, 0.1), 0.9);
        
        const g = this.curveGraphics;
        const padding = this.config.padding;
        const graphWidth = this.graphWidth;
        const graphHeight = this.graphHeight;
        const height = this.config.height;
        
        g.clear();
        g.lineStyle(3, this.config.curveColor, 1);
        
        // 计算并绘制曲线
        const stepCount = 200; // 增加采样点使曲线更平滑
        
        // 绘制曲线（从左到右）
        for (let i = 0; i <= stepCount; i++) {
            // 将i映射到-1到1的范围
            const x = (i / stepCount) * 2 - 1;
            
            // 计算输出值
            const y = this.calculateExpValue(x, exp);
            
            // 将坐标映射到画布坐标
            const canvasX = padding + ((i / stepCount) * graphWidth);
            const canvasY = (height / 2) - (y * graphHeight / 2);
            
            if (i === 0) {
                g.moveTo(canvasX, canvasY);
            } else {
                g.lineTo(canvasX, canvasY);
            }
        }
        
        // 保存当前EXP值
        this.config.expValue = exp;
    }

    
    /**
     * 获取当前EXP值
     * @returns {number} 当前EXP值
     */
    getExpValue() {
        return this.config.expValue;
    }
    
    /**
     * 设置EXP值并更新曲线
     * @param {number} expValue - EXP值 (0.1-0.9)
     */
    setExpValue(expValue:any) {
        this.updateCurve(expValue);
    }

    /**
    * 调整画布大小
    */
    resize() {
        if (!this.initialized) return;
        
        this.initContainer();
        // 更新Pixi应用大小
        this.app.renderer.resize(this.config.width, this.config.height);
        
        // 重新绘制所有元素
        this.axisGraphics.clear();
        this.diagonalGraphics.clear();
        this.arrowGraphics.clear();
        this.labelContainer.removeChildren();
        this.curveGraphics.clear();
        this.centerCircleGraphics.clear();
        
        this.drawAxes();
        this.drawDiagonal();
        this.drawArrowsAndLabels();
        this.updateCurve(this.config.expValue);
        this.drawCenterCircle();
    }
}